// Port of SuiteModel/SeededRandom.swift (and the draws NameGenerator.swift takes): SplitMix64, a small repeatable
// generator drawn alike here and on the Mac, so anything seeded alike (a map's pencil wobble, the name generator's
// picks, a test's draws) comes out alike on every device.

/** A source of 64-bit random values (`RandomNumberGenerator`). */
export interface RandomSource { next(): bigint }

const MASK64 = (1n << 64n) - 1n;

/** SplitMix64. Seeded with a number, or with a string (an element's id and what's being drawn) by `SplitMix64.seed`. */
export class SplitMix64 implements RandomSource {
  private state: bigint;
  constructor(seed: bigint | number | string) {
    this.state = typeof seed === 'string' ? SplitMix64.seed(seed) : BigInt.asUintN(64, BigInt(seed));
  }

  next(): bigint {
    this.state = (this.state + 0x9e3779b97f4a7c15n) & MASK64;
    let z = this.state;
    z = ((z ^ (z >> 30n)) * 0xbf58476d1ce4e5b9n) & MASK64;
    z = ((z ^ (z >> 27n)) * 0x94d049bb133111ebn) & MASK64;
    return z ^ (z >> 31n);
  }

  /** A number in [0, 1) from the top 53 bits: exact, so the same on every platform. */
  unit(): number {
    return Number(this.next() >> 11n) / 2 ** 53;
  }

  /** A number in [-1, 1). */
  symmetric(): number {
    return this.unit() * 2 - 1;
  }

  /** FNV-1a, 64-bit, over the string's UTF-8 bytes. */
  static seed(text: string): bigint {
    let hash = 0xcbf29ce484222325n;
    for (const byte of new TextEncoder().encode(text)) {
      hash ^= BigInt(byte);
      hash = (hash * 0x100000001b3n) & MASK64;
    }
    return hash;
  }
}

/** Swift's `next(upperBound:)` for UInt64: Lemire's method. */
export function nextBelow(rng: RandomSource, upperBound: number): number {
  const upper = BigInt(upperBound);
  let m = (rng.next() & MASK64) * upper;
  if ((m & MASK64) < upper) {
    const t = ((1n << 64n) - upper) % upper;
    while ((m & MASK64) < t) m = (rng.next() & MASK64) * upper;
  }
  return Number(m >> 64n);
}

/** `randomElement(using:)`: null for an empty array; otherwise one draw, even for a single element. */
export function randomElement<T>(array: readonly T[], rng: RandomSource): T | null {
  if (!array.length) return null;
  return array[nextBelow(rng, array.length)];
}

/** A random source from `crypto.getRandomValues` (`SystemRandomNumberGenerator`). */
export const systemRandom: RandomSource = {
  next() {
    const a = new BigUint64Array(1);
    crypto.getRandomValues(a);
    return a[0];
  },
};
